import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Output, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';
import { LoaderService } from '../../../../core/service/loader.service';
import { ProfileService } from '../../../../core/service/profile/profile.service';
import { PopupServiceService } from '../../../../core/service/popup/popup-service.service';

@Component({
  selector: 'app-two-factor-verification',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './two-factor-verification.component.html',
  styleUrl: './two-factor-verification.component.scss'
})
export class TwoFactorVerificationComponent implements AfterViewInit {
  dialogRef = inject(MatDialogRef<TwoFactorVerificationComponent>);
  private _unsubscribeAll = new Subject<void>();
  private _profileService = inject(ProfileService);
  private _loaderService = inject(LoaderService);
  private popupService = inject(PopupServiceService);
  private _fb = inject(FormBuilder);

  @Output() onSuccess = new EventEmitter<void>();

  @ViewChildren('digitInput') private _digitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  private readonly _digitControlNames = ['digit1', 'digit2', 'digit3', 'digit4', 'digit5', 'digit6'];

  form: FormGroup = this._fb.group({
    digit1: ['', [Validators.required, Validators.maxLength(1)]],
    digit2: ['', [Validators.required, Validators.maxLength(1)]],
    digit3: ['', [Validators.required, Validators.maxLength(1)]],
    digit4: ['', [Validators.required, Validators.maxLength(1)]],
    digit5: ['', [Validators.required, Validators.maxLength(1)]],
    digit6: ['', [Validators.required, Validators.maxLength(1)]],
  });

  get twoFactorAuthCode(): string {
    return Object.values(this.form.value).join('');
  }

  ngAfterViewInit(): void {
    this._digitInputs.first?.nativeElement.focus();
  }

  // Scoped to this component's own inputs (via ViewChildren) rather than
  // document.getElementById, since the host page behind the dialog can have
  // its own elements with the same ids (e.g. away-lock's PIN inputs).
  private _focusDigit(index: number): void {
    this._digitInputs.toArray()[index]?.nativeElement.focus();
  }

  onInput(event: any, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value.length > 1) {
      input.value = value.slice(0, 1);
      this.form.get(input.getAttribute('formControlName')!)?.setValue(input.value);
    }

    if (value !== '') {
      this._focusDigit(index + 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');

    // Only allow digits
    const cleanText = pastedText.replace(/\D/g, '').substring(0, 6);

    if (cleanText) {
      const digits = this._digitControlNames;
      const inputs = this._digitInputs.toArray();

      for (let i = 0; i < cleanText.length && i < digits.length; i++) {
        // Update form value
        this.form.get(digits[i])?.setValue(cleanText[i]);

        // Update DOM input value manually to avoid sync issues.
        if (inputs[i]) {
          inputs[i].nativeElement.value = cleanText[i];
        }
      }

      // Focus on the next empty input, or the last input if full
      const nextFocusIndex = Math.min(cleanText.length, digits.length - 1);
      this._focusDigit(nextFocusIndex);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;
      if (input.value === '') {
        this._focusDigit(index - 1);
      }
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this._focusDigit(index - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this._focusDigit(index + 1);
    }

    // Prevent non-numeric input
    if (!/^[0-9]$/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Delete' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  showTwofactor = true;
  data: any;
  verifyCode() {
    if (this.form.invalid) {
      return;
    }
    const code = this.twoFactorAuthCode;
    const payload = {
      security: code
    };
    this._loaderService.show();
    if (this.showTwofactor) {
      this._profileService.verifyTwoFactorCode(code)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: (res) => {
            this._loaderService.hide();
            this.dialogRef.close(true);
            this.onSuccess.emit();
          },
          error: (err) => {
            this._loaderService.hide();
            this.popupService.successAndErrorDialog('error', 'Verification failed Entered valid code');
          }
        });

    } else {
      this._profileService.adminMaskInfo(this.data?.application_id, payload)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe({
          next: (res) => {
            this._loaderService.hide();
            this.dialogRef.close(res);
          },
          error: (err) => {
            this._loaderService.hide();
            this.popupService.successAndErrorDialog('error', 'Verification failed Entered valid code');
          }
        });
    }
  }
}
